/**
 * API Utility Functions
 *
 * Collection of utility functions used across API operations for file handling,
 * date formatting, and filename cleanup.
 */

/**
 * Downloads a blob as a file by creating a temporary download link
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);

  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Extracts file extension from HTTP Content-Type header
 */
export const getFileExtensionFromContentType = (
  contentType: string
): string => {
  if (contentType.includes('application/pdf')) return 'pdf';
  if (contentType.includes('application/zip')) return 'zip';
  if (contentType.includes('application/vnd.openxmlformats')) return 'xlsx';
  return 'xlsx'; // default fallback
};

/**
 * Formats a date string to DD-MMM-YYYY format (e.g., "15-Jan-2024")
 */
export const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

/**
 * Formats current date to DDMMYY_HHMM format for timestamps (e.g., "150124_1430")
 */
export const formatDateTimestamp = (date: Date = new Date()): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}${month}${year}_${hours}${minutes}`;
};

/**
 * Cleans up filename by removing multiple consecutive separators and trailing separators
 */
export const cleanupFilename = (filename: string): string => {
  return filename
    .replace(/_{2,}/g, '_') // Multiple underscores → single underscore
    .replace(/-{2,}/g, '-') // Multiple hyphens → single hyphen
    .replace(/\s{2,}/g, ' ') // Multiple spaces → single space
    .replace(/^[_\-\s]+|[_\-\s]+$/g, '') // Remove leading/trailing separators
    .replace(/[_\-\s]+\./g, '.'); // Remove separator before extension
};

/**
 * Report type name mappings for display and filenames
 */
export const REPORT_TYPE_NAMES: Record<string, string> = {
  holdings: 'Holdings',
  highlights: 'Highlights',
  standard: 'Standard',
  custom: 'Custom',
  custom_high_level: 'Custom High Level',
};

/**
 * Generates a filename for OTC or Holdings backup files with timestamp
 */
export const generateBackupFilename = (type: 'OTC' | 'Holdings'): string => {
  const dateTimeString = formatDateTimestamp();
  const extension = type === 'OTC' ? 'json' : 'xlsx';
  return `Wise_Capital_${type}_Backup_${dateTimeString}.${extension}`;
};

/**
 * Generates a filename for vault processed files with timestamp
 * Backend always returns a ZIP file containing aggregated_vaults.xlsx + individuals folder
 */
export const generateVaultProcessedFilename = (): string => {
  const now = new Date();
  const dateTimeString = now
    .toISOString()
    .slice(0, 19)
    .replace(/[:\-T]/g, '_');
  return `Vault_Processed_${dateTimeString}.zip`;
};

/**
 * Downloads a blob and returns a success message object
 */
export const downloadBlobWithMessage = (
  blob: Blob,
  filename: string
): { message: string } => {
  downloadBlob(blob, filename);
  return { message: 'File downloaded successfully' };
};

/**
 * Parameters for generating a report filename
 */
export interface ReportFilenameParams {
  filenameTemplate?: string;
  portfolios: Array<{ clientName?: string | null }>;
  reportType: string;
  fromDate?: string;
  toDate?: string;
  fileExtension: string;
}

/**
 * Generates a filename for reports with optional template substitution
 */
export const generateReportFilename = (
  params: ReportFilenameParams
): string => {
  const {
    filenameTemplate,
    portfolios,
    reportType,
    fromDate,
    toDate,
    fileExtension,
  } = params;

  if (filenameTemplate) {
    // Substitute variables in template
    const portfolioName =
      portfolios.length === 1 && portfolios[0].clientName
        ? portfolios[0].clientName
        : portfolios.length > 1
          ? 'Multiple_Portfolios'
          : 'Portfolio';

    let filename = cleanupFilename(
      filenameTemplate
        .replace(/{portfolio_name}/g, portfolioName)
        .replace(/{report_type}/g, reportType)
        .replace(/{from_date}/g, formatDate(fromDate))
        .replace(/{to_date}/g, formatDate(toDate))
    );

    // Add file extension if not already present
    if (!filename.endsWith(`.${fileExtension}`)) {
      filename += `.${fileExtension}`;
    }

    return filename;
  } else {
    // Fallback to default naming
    const reportTypeName = REPORT_TYPE_NAMES[reportType] || reportType;
    return `${reportTypeName} Report.${fileExtension}`;
  }
};

/**
 * File Validation
 */

/**
 * Validate if a file is a valid Excel file
 */
export const validateExcelFile = (
  filename: string
): { isValid: boolean; error?: string } => {
  const lowerFilename = filename.toLowerCase();
  const isValid =
    lowerFilename.endsWith('.xlsx') || lowerFilename.endsWith('.xls');

  if (!isValid) {
    return {
      isValid: false,
      error: 'Invalid file type. Only .xlsx and .xls files are allowed',
    };
  }

  return { isValid: true };
};
